import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import toastr from "toastr";
import "toastr/build/toastr.css";
import { API_WEB_URLS } from "../constants/constAPI";
import { useNavigate } from "react-router-dom";

const SignalRNotificationContext = createContext(null);

const STORAGE_KEY = "admin_booking_notifications";
const MAX_STORED_NOTIFICATIONS = 25;

// Synthesize an attention chime using Web Audio API (cross-browser, zero assets required)
const playNotificationChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // First Tone (D5 - 587.33 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0.25, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Second Tone (A5 - 880 Hz) - Higher resonant note
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880, now + 0.14);
    gain2.gain.setValueAtTime(0.35, now + 0.14);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.14);
    osc2.stop(now + 0.65);
  } catch (err) {
    console.warn("[SignalRNotification] Audio chime failed:", err);
  }
};

export const SignalRNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const subscribersRef = useRef(new Set());
  const connectionRef = useRef(null);

  // Subscribe callback for screens like AssignDriver.js
  const subscribeToNewBookings = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn("Error clearing notifications storage:", e);
    }
  }, []);

  // Handle incoming booking from SignalR
  const handleNewBooking = useCallback(
    (bookingData) => {
      console.log("🚨 [SignalRNotification] New Booking Received:", bookingData);

      // Play sound chime
      playNotificationChime();

      const bookingId = bookingData?.BookingId || bookingData?.bookingId || "New";
      const customerName = bookingData?.CustomerName || bookingData?.customerName || "Customer";
      const customerMobile = bookingData?.CustomerMobile || bookingData?.customerMobile || "";
      const pickup = bookingData?.PickupAddress || bookingData?.pickupAddress || "Pickup Location";
      const drop = bookingData?.DropAddress || bookingData?.dropAddress || "Drop Location";
      const fare = bookingData?.Price ?? bookingData?.price ?? 0;
      const km = bookingData?.TotalTripKM ?? bookingData?.totalTripKM ?? 0;
      const dateStr = bookingData?.BookingDate || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      const newNotification = {
        id: `noti-${Date.now()}-${bookingId}`,
        bookingId: String(bookingId),
        customerName,
        customerMobile,
        pickup,
        drop,
        fare,
        km,
        time: dateStr,
        timestamp: Date.now(),
        isRead: false,
      };

      // Update state & persistence
      setNotifications((prev) => {
        const updated = [newNotification, ...prev].slice(0, MAX_STORED_NOTIFICATIONS);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
          console.warn("Failed to persist notification:", e);
        }
        return updated;
      });
      setUnreadCount((prev) => prev + 1);

      // Notify all active page subscribers (e.g. AssignDriver to auto-fetch table)
      subscribersRef.current.forEach((cb) => {
        try {
          cb(bookingData);
        } catch (cbErr) {
          console.error("[SignalRNotification] Subscriber error:", cbErr);
        }
      });

      // Show toastr notification with quick action
      toastr.options = {
        closeButton: true,
        debug: false,
        newestOnTop: true,
        progressBar: true,
        positionClass: "toast-top-right",
        preventDuplicates: false,
        onclick: () => {
          navigate("/AssignDriver");
        },
        showDuration: "300",
        hideDuration: "1000",
        timeOut: "12000",
        extendedTimeOut: "5000",
        showEasing: "swing",
        hideEasing: "linear",
        showMethod: "fadeIn",
        hideMethod: "fadeOut",
      };

      const pickupShort = pickup.length > 40 ? pickup.substring(0, 37) + "..." : pickup;
      const dropShort = drop.length > 40 ? drop.substring(0, 37) + "..." : drop;

      const toastHtml = `
        <div style="font-family: inherit; font-size: 12.5px; line-height: 1.4;">
          <div style="font-weight: 600; font-size: 13.5px; margin-bottom: 4px; color: #fff;">
            🚕 Ride #${bookingId} Booked!
          </div>
          <div>👤 <b>${customerName}</b> ${customerMobile ? `(${customerMobile})` : ""}</div>
          <div>📍 <b>Pickup:</b> ${pickupShort}</div>
          <div>🏁 <b>Drop:</b> ${dropShort}</div>
          <div>💰 <b>₹${fare}</b> (${km} KM)</div>
          <div style="margin-top: 8px;">
            <span style="display: inline-block; background-color: #28a745; color: #fff; padding: 3px 8px; border-radius: 12px; font-weight: bold; font-size: 11px;">
              👉 Click to Assign Driver
            </span>
          </div>
        </div>
      `;

      toastr.info(toastHtml, "New Booking Alert!");
    },
    [navigate]
  );

  // Initialize SignalR connection
  useEffect(() => {
    const hubUrl = API_WEB_URLS.BASE
      ? API_WEB_URLS.BASE.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub")
      : "https://apikirayago.shinewellsofttech.co.in/bookingHub";

    console.log("🔌 [SignalRNotification] Connecting to:", hubUrl);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    const registerAdmin = async () => {
      try {
        await connection.invoke("RegisterAdmin");
        console.log("✅ [SignalRNotification] Registered in Admins group");
      } catch (err) {
        console.warn("⚠️ [SignalRNotification] RegisterAdmin failed, retrying on demand:", err);
      }
    };

    connection.on("AdminNewBookingReceived", (data) => {
      handleNewBooking(data);
    });

    // Also listen to general booking availability as fallback
    connection.on("NewBookingAvailable", () => {
      console.log("🔔 [SignalRNotification] NewBookingAvailable trigger received");
      subscribersRef.current.forEach((cb) => {
        try {
          cb({ isGeneralRefresh: true });
        } catch (e) {
          console.error(e);
        }
      });
    });

    connection.onreconnected(() => {
      console.log("🔄 [SignalRNotification] Reconnected to BookingHub");
      setIsConnected(true);
      registerAdmin();
    });

    connection.onreconnecting(() => {
      console.log("⚠️ [SignalRNotification] Reconnecting to BookingHub...");
      setIsConnected(false);
    });

    connection.onclose(() => {
      console.log("❌ [SignalRNotification] Connection closed");
      setIsConnected(false);
    });

    connection
      .start()
      .then(() => {
        setIsConnected(true);
        registerAdmin();
      })
      .catch((err) => {
        console.warn("⚠️ [SignalRNotification] Initial connection failed:", err);
      });

    connectionRef.current = connection;

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [handleNewBooking]);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    markAllAsRead,
    clearNotifications,
    subscribeToNewBookings,
    triggerManualAlert: handleNewBooking, // Can be called manually for testing
  };

  return (
    <SignalRNotificationContext.Provider value={value}>
      {children}
    </SignalRNotificationContext.Provider>
  );
};

export const useSignalRNotifications = () => {
  const context = useContext(SignalRNotificationContext);
  if (!context) {
    throw new Error("useSignalRNotifications must be used within a SignalRNotificationProvider");
  }
  return context;
};

export default SignalRNotificationContext;
